import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/state/auth";
import { apiRequest } from "@/utils/apiClient";
import { Author, AuthorInput, AuthorUpdateInput } from "@/types/authors";

const QUERY_ROOT = ["admin", "authors"] as const;

export const useAuthors = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT],
    queryFn: async () => {
      const data = await apiRequest<{ authors: Author[] }>("/api/admin/authors");
      return data.authors;
    },
    enabled: Boolean(profile),
  });
};

export const useAuthor = (authorId: string | null) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, authorId],
    queryFn: async () => {
      if (!authorId) {
        return null;
      }
      const data = await apiRequest<{ author: Author }>(`/api/admin/authors/${authorId}`);
      return data.author;
    },
    enabled: Boolean(profile && authorId),
  });
};

export const useCreateAuthor = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AuthorInput) => {
      const { author } = await apiRequest<{ author: Author }>("/api/admin/authors", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return author;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT] });
    },
  });
};

export const useUpdateAuthor = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ authorId, payload }: { authorId: string; payload: AuthorUpdateInput }) => {
      const { author } = await apiRequest<{ author: Author }>(`/api/admin/authors/${authorId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      return author;
    },
    onSuccess: (author) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT] });
      client.setQueryData([...QUERY_ROOT, author.authorId], author);
    },
  });
};

export const useDeleteAuthor = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (authorId: string) => {
      await apiRequest<void>(`/api/admin/authors/${authorId}`, { method: "DELETE" });
      return authorId;
    },
    onSuccess: (authorId) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT] });
      client.removeQueries({ queryKey: [...QUERY_ROOT, authorId] });
    },
  });
};
